import i18next, { createInstance, type i18n, type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import { FALLBACK_LOCALE, LOCALE_CODES } from "@/engine/support/locales";

/**
 * The catalogue, loaded into i18next. Importing this module is what makes
 * `useTranslation()` return words rather than keys, which is why the test setup
 * imports it too — a component rendered without it would be checked against
 * `chat.title` instead of "Chat", and every assertion would have to know that.
 *
 * i18next carries the lookup rather than a hand-rolled one, because the parts
 * that are not the lookup are the expensive ones: plural categories per locale
 * out of `Intl.PluralRules`, interpolation, and a fallback chain that resolves
 * a missing key instead of rendering it. Covering the Google Ads languages
 * (#254) needs all three, in every locale, at once.
 *
 * **The resources are every directory under `src/locales/`**, gathered by
 * `import.meta.glob` rather than named one import at a time (#254): a locale is
 * added by dropping in a `<code>/common.json`, and `locales.ts` plus the copy
 * gate are what say whether that code is offered and complete. The keyed path
 * `../locales/<code>/common.json` yields `<code>`.
 *
 * **The server gets one instance per request and the client keeps the
 * singleton.** The language is resolved before the render now (#310), so two
 * requests being served at once are two languages, and a shared
 * `i18next.language` would hand one visitor the other's. On the client there is
 * only ever one visitor, and the singleton is what lets the error boundaries
 * read a catalogue at all: they render outside every provider, which is exactly
 * when a visitor most needs to read what happened.
 */

export const DEFAULT_NAMESPACE = "common";

const catalogues = import.meta.glob<Record<string, unknown>>(
  "../locales/*/common.json",
  { eager: true, import: "default" },
);

const resources: InitOptions["resources"] = {};
for (const [path, catalogue] of Object.entries(catalogues)) {
  const code = path.replace("../locales/", "").replace("/common.json", "");
  resources[code] = { [DEFAULT_NAMESPACE]: catalogue };
}

function options(locale: string): InitOptions {
  return {
    resources,
    lng: locale,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: [...LOCALE_CODES],
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE],
    // React escapes what it renders; escaping again turns an apostrophe in the
    // copy into `&#39;` in front of the visitor.
    interpolation: { escapeValue: false },
    // A key no catalogue carries is a bug rather than a translation gap —
    // `fallbackLng` already covers a locale that is merely behind. Rendering
    // the last segment keeps a word on the screen instead of `room.chat.title`.
    parseMissingKeyHandler: (key: string) => key.split(".").pop() ?? key,
  };
}

void i18next.use(initReactI18next).init(options(FALLBACK_LOCALE));

/**
 * A fresh instance in one locale, for one server render.
 *
 * It is deliberately not registered with `initReactI18next`: that plugin
 * installs the instance as `react-i18next`'s global default, which is the
 * shared mutable state this exists to avoid. The render reaches it through
 * `I18nextProvider` instead.
 *
 * The catalogues are the same objects the singleton holds — they are frozen
 * JSON imports, so the per-request cost is the instance and not the copy.
 */
export function createI18n(locale: string): i18n {
  const instance = createInstance(options(locale));
  void instance.init();
  return instance;
}

/**
 * Put the singleton into a locale and hand it back. Synchronous, because every
 * catalogue is already in memory: i18next only defers `changeLanguage` while it
 * is waiting on a backend, and there is none here. That matters — this is
 * called during the first client render, and an asynchronous switch would
 * render English into a document the server wrote in Russian.
 */
export function applyLocale(locale: string): i18n {
  if (i18next.language !== locale) void i18next.changeLanguage(locale);
  return i18next;
}

export default i18next;
